import React, { useState, useRef, useEffect } from 'react';
import { 
    ChevronDownIcon, MenuIcon, XIcon, SunIcon, MoonIcon,
    ColorfulPdfImageIcon, ColorfulPdfEditorIcon, ColorfulOcrIcon,
    ColorfulPageExtractIcon, ColorfulPdfSplitIcon, ColorfulPdfCropIcon,
    ColorfulRotateIcon, ColorfulPageNumIcon, ColorfulBookletIcon,
    ColorfulImageCompressorIcon, ColorfulPdfRedactorIcon, ColorfulPdfSecurityIcon,
    ColorfulPdfSignIcon, ColorfulPdfGrayscaleIcon, ColorfulInstagramGridIcon,
    ColorfulImageBlurIcon, ColorfulImageUpscalerIcon, ColorfulImageEditorIcon,
    ColorfulAiHumanizerIcon, ColorfulAiTextDetectorIcon, ColorfulVoiceToTextIcon,
    ColorfulTweetIcon, ColorfulFancyFontIcon, ColorfulAudioCutterIcon,
    ColorfulYoutubeTrendIcon, ColorfulFacebookIcon, ColorfulSerpIcon,
    ColorfulSlugIcon, ColorfulDnsIcon, ColorfulSslIcon, ColorfulHttpHeaderIcon,
    ColorfulDiffIcon, ColorfulJwtIcon, ColorfulCurlIcon, ColorfulCronIcon,
    ColorfulJsonIcon, ColorfulFreelanceRateIcon, ColorfulFuelTripIcon,
    ColorfulCalculatorIcon, ColorfulCurrencyIcon, ColorfulAgeCalculatorIcon,
    ColorfulBmiIcon, ColorfulTextEditorIcon, ColorfulWordCounterIcon,
    ColorfulRobotsTxtIcon, ColorfulSitemapGeneratorIcon, ColorfulXmlToJsonIcon,
    ColorfulBase64ToImageIcon, ColorfulTikTokIcon, ColorfulInstagramIcon
} from './Icons';

interface NavDropdownItem {
    label: string;
    path: string;
    icon?: React.ComponentType<{ className?: string }>;
    badge?: string;
    isMore?: boolean;
}

interface NavColumn {
    title: string;
    items: NavDropdownItem[];
}

interface NavDropdownMenu {
    id: string;
    label: string;
    columns: NavColumn[];
}

interface NavbarProps {
    navigateTo: (path: string) => void;
    isDarkMode: boolean;
    toggleDarkMode: () => void;
    activeTool?: string;
}

export const Navbar: React.FC<NavbarProps> = ({
    navigateTo,
    isDarkMode,
    toggleDarkMode
}) => {
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
    const [mobileExpandedGroup, setMobileExpandedGroup] = useState<string | null>(null);
    const navRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<any>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleMouseEnter = (id: string) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setOpenDropdown(id);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setOpenDropdown(null);
        }, 200);
    };

    const handleLinkClick = (path: string) => {
        setOpenDropdown(null);
        setIsMobileMenuOpen(false);
        navigateTo(path);
    };

    // Navigation Structure with Comprehensive Tool Coverage & More... links
    const navMenus: NavDropdownMenu[] = [
        {
            id: 'convert-pdf',
            label: 'CONVERT PDF',
            columns: [
                {
                    title: 'CONVERT TO PDF',
                    items: [
                        { label: 'JPG to PDF', path: '/tool/image-to-pdf', icon: ColorfulPdfImageIcon },
                        { label: 'WORD to PDF', path: '/tool/doc-to-pdf', icon: ColorfulPdfEditorIcon },
                        { label: 'POWERPOINT to PDF', path: '/tool/ppt-to-pdf', icon: ColorfulPdfEditorIcon },
                        { label: 'EXCEL to PDF', path: '/tool/excel-to-pdf', icon: ColorfulPdfEditorIcon },
                        { label: 'HTML to PDF', path: '/tool/html-to-pdf', icon: ColorfulPdfEditorIcon },
                        { label: 'More Convert to PDF...', path: '/tool/pdf-image', isMore: true },
                    ]
                },
                {
                    title: 'CONVERT FROM PDF',
                    items: [
                        { label: 'Translate PDF (AI)', path: '/tool/pdf-translate', icon: ColorfulAiHumanizerIcon, badge: 'AI' },
                        { label: 'PDF to JPG / PNG', path: '/tool/pdf-to-image', icon: ColorfulPdfImageIcon },
                        { label: 'PDF to TEXT', path: '/tool/pdf-to-text', icon: ColorfulPdfEditorIcon },
                        { label: 'PDF OCR (Scan to Text)', path: '/tool/pdf-ocr', icon: ColorfulOcrIcon, badge: 'Pro' },
                        { label: 'PDF Page Extractor', path: '/tool/pdf-page-extractor', icon: ColorfulPageExtractIcon },
                        { label: 'More Convert from PDF...', path: '/tool/pdf-image', isMore: true },
                    ]
                }
            ]
        },
        {
            id: 'all-pdf-tools',
            label: 'ALL PDF TOOLS',
            columns: [
                {
                    title: 'ORGANIZE & EDIT',
                    items: [
                        { label: 'Merge PDF', path: '/tool/pdf-merge', icon: ColorfulPdfSplitIcon },
                        { label: 'Split PDF', path: '/tool/pdf-split', icon: ColorfulPdfSplitIcon },
                        { label: 'Crop PDF Margins', path: '/tool/pdf-crop', icon: ColorfulPdfCropIcon },
                        { label: 'Rotate PDF', path: '/tool/pdf-rotate', icon: ColorfulRotateIcon },
                        { label: 'PDF Page Numberer', path: '/tool/pdf-page-numberer', icon: ColorfulPageNumIcon },
                        { label: 'PDF Booklet Maker', path: '/tool/pdf-booklet-maker', icon: ColorfulBookletIcon },
                        { label: 'More PDF Tools...', path: '/tool/pdf-editor', isMore: true },
                    ]
                },
                {
                    title: 'SECURITY & OPTIMIZE',
                    items: [
                        { label: 'Compress PDF', path: '/tool/pdf-compress', icon: ColorfulImageCompressorIcon, badge: 'Popular' },
                        { label: 'PDF Redactor (Blackout)', path: '/tool/pdf-redactor', icon: ColorfulPdfRedactorIcon },
                        { label: 'Password Protect PDF', path: '/tool/pdf-security', icon: ColorfulPdfSecurityIcon },
                        { label: 'Unlock PDF', path: '/tool/pdf-unlock', icon: ColorfulPdfSecurityIcon },
                        { label: 'Electronic Sign PDF', path: '/tool/pdf-sign', icon: ColorfulPdfSignIcon },
                        { label: 'PDF Grayscale', path: '/tool/pdf-grayscale', icon: ColorfulPdfGrayscaleIcon },
                        { label: 'More Security Tools...', path: '/tool/pdf-security', isMore: true },
                    ]
                }
            ]
        },
        {
            id: 'image-tools',
            label: 'IMAGE TOOLS',
            columns: [
                {
                    title: 'EDIT & OPTIMIZE',
                    items: [
                        { label: 'Image Compressor (WebP)', path: '/tool/image-compressor', icon: ColorfulImageCompressorIcon },
                        { label: 'Photo Studio Editor', path: '/tool/image-editor', icon: ColorfulImageEditorIcon },
                        { label: 'Background Remover', path: '/tool/background-remover', icon: ColorfulImageEditorIcon },
                        { label: '4K AI Image Upscaler', path: '/tool/image-upscaler', icon: ColorfulImageUpscalerIcon, badge: 'AI' },
                        { label: 'Image to SVG Vectorizer', path: '/tool/image-to-svg', icon: ColorfulImageEditorIcon },
                        { label: 'More Image Tools...', path: '/tool/image-editor', isMore: true },
                    ]
                },
                {
                    title: 'CREATIVE & SOCIAL',
                    items: [
                        { label: 'Instagram 9-Grid Splitter', path: '/tool/instagram-grid-splitter', icon: ColorfulInstagramGridIcon },
                        { label: 'Image Blur & Pixelate', path: '/tool/image-blur-tool', icon: ColorfulImageBlurIcon },
                        { label: 'Retro Polaroid Maker', path: '/tool/polaroid-maker', icon: ColorfulImageEditorIcon },
                        { label: 'Meme Generator', path: '/tool/meme-generator', icon: ColorfulImageEditorIcon },
                        { label: 'Color Palette Extractor', path: '/tool/color-palette', icon: ColorfulImageEditorIcon },
                        { label: 'More Creative Tools...', path: '/tool/image-editor', isMore: true },
                    ]
                }
            ]
        },
        {
            id: 'ai-creator',
            label: 'AI & CREATORS',
            columns: [
                {
                    title: 'AI & WRITING SUITE',
                    items: [
                        { label: 'AI Text Humanizer', path: '/tool/ai-humanizer', icon: ColorfulAiHumanizerIcon, badge: 'Hot' },
                        { label: 'AI Text Detector', path: '/tool/ai-text-detector', icon: ColorfulAiTextDetectorIcon },
                        { label: 'Article Rewriter', path: '/tool/article-rewriter', icon: ColorfulAiHumanizerIcon },
                        { label: 'Text to Speech (TTS)', path: '/tool/text-to-speech', icon: ColorfulVoiceToTextIcon },
                        { label: 'Voice to Text Transcription', path: '/tool/voice-to-text', icon: ColorfulVoiceToTextIcon },
                        { label: 'More AI Writing Tools...', path: '/tool/online-text-editor', isMore: true },
                    ]
                },
                {
                    title: 'SOCIAL & VIRAL MEDIA',
                    items: [
                        { label: 'Tweet to Aesthetic Image', path: '/tool/tweet-to-image', icon: ColorfulTweetIcon },
                        { label: '30+ Fancy Font Generator', path: '/tool/fancy-font-generator', icon: ColorfulFancyFontIcon },
                        { label: 'Audio / MP3 Waveform Cutter', path: '/tool/audio-cutter', icon: ColorfulAudioCutterIcon },
                        { label: 'YouTube SEO Suite', path: '/tool/youtube-thumbnail-downloader', icon: ColorfulYoutubeTrendIcon },
                        { label: 'Facebook Video Downloader', path: '/tool/facebook-downloader', icon: ColorfulFacebookIcon },
                        { label: 'More Social Downloaders...', path: '/tool/instagram-downloader', isMore: true },
                    ]
                }
            ]
        },
        {
            id: 'dev-seo',
            label: 'DEV & SEO',
            columns: [
                {
                    title: 'WEB & SEO TOOLS',
                    items: [
                        { label: 'Google SERP Simulator', path: '/tool/serp-preview-generator', icon: ColorfulSerpIcon, badge: 'New' },
                        { label: 'SEO Slug & URL Maker', path: '/tool/seo-slug-generator', icon: ColorfulSlugIcon },
                        { label: 'DNS Record Lookup (DoH)', path: '/tool/dns-lookup', icon: ColorfulDnsIcon },
                        { label: 'SSL Certificate & Expiry', path: '/tool/ssl-checker', icon: ColorfulSslIcon },
                        { label: 'HTTP Status & Headers', path: '/tool/http-header-analyzer', icon: ColorfulHttpHeaderIcon },
                        { label: 'More SEO Tools...', path: '/tool/robots-txt-tool', isMore: true },
                    ]
                },
                {
                    title: 'DEVELOPER UTILITIES',
                    items: [
                        { label: 'Side-by-Side Diff Checker', path: '/tool/diff-checker', icon: ColorfulDiffIcon },
                        { label: 'JWT Token Decoder', path: '/tool/jwt-decoder', icon: ColorfulJwtIcon },
                        { label: 'cURL to Code Converter', path: '/tool/curl-converter', icon: ColorfulCurlIcon },
                        { label: 'Cron Expression Generator', path: '/tool/cron-generator', icon: ColorfulCronIcon },
                        { label: 'JSON Formatter & Validator', path: '/tool/json-formatter', icon: ColorfulJsonIcon },
                        { label: 'More Dev Utilities...', path: '/tool/xml-to-json', isMore: true },
                    ]
                }
            ]
        },
        {
            id: 'calculators',
            label: 'CALCULATORS',
            columns: [
                {
                    title: 'FINANCIAL & RATES',
                    items: [
                        { label: 'Freelance Hourly Rate', path: '/tool/freelance-rate-calculator', icon: ColorfulFreelanceRateIcon },
                        { label: 'Trip Fuel & Bill Splitter', path: '/tool/trip-fuel-calculator', icon: ColorfulFuelTripIcon },
                        { label: 'Loan & Mortgage EMI', path: '/tool/loan-calculator', icon: ColorfulCalculatorIcon },
                        { label: 'Live Currency Converter', path: '/tool/currency', icon: ColorfulCurrencyIcon },
                        { label: 'More Financial Tools...', path: '/tool/discount-calculator', isMore: true },
                    ]
                },
                {
                    title: 'DAILY LIFE UTILITIES',
                    items: [
                        { label: 'Chronological Age Calculator', path: '/tool/age-calculator', icon: ColorfulAgeCalculatorIcon },
                        { label: 'BMI Health Calculator', path: '/tool/bmi-calculator', icon: ColorfulBmiIcon },
                        { label: 'Stopwatch & Timer', path: '/tool/stopwatch-timer', icon: ColorfulCalculatorIcon },
                        { label: 'Time Converter', path: '/tool/time-converter', icon: ColorfulCalculatorIcon },
                        { label: 'More Converters...', path: '/tool/length-converter', isMore: true },
                    ]
                }
            ]
        }
    ];

    return (
        <header className="sticky top-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800 shadow-xs transition-colors duration-300">
            <div ref={navRef} className="max-w-[92rem] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 sm:h-18">
                    
                    {/* Brand Logo */}
                    <div className="flex items-center gap-6">
                        <a 
                            href="/" 
                            onClick={(e) => { e.preventDefault(); handleLinkClick('/'); }}
                            className="flex items-center gap-2.5 text-xl font-black tracking-tight text-slate-900 dark:text-white group flex-shrink-0 cursor-pointer"
                        >
                            <span className="w-9 h-9 rounded-2xl bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-md shadow-cyan-500/30 group-hover:scale-105 transition-transform">
                                ⚡
                            </span>
                            <span className="bg-gradient-to-r from-slate-900 via-cyan-900 to-slate-900 dark:from-white dark:via-cyan-200 dark:to-white bg-clip-text text-transparent">
                                ToolsCopilot
                            </span>
                        </a>
                    </div>

                    {/* Desktop Mega Dropdowns */}
                    <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
                        {navMenus.map((menu) => {
                            const isOpen = openDropdown === menu.id;
                            return (
                                <div 
                                    key={menu.id} 
                                    className="relative"
                                    onMouseEnter={() => handleMouseEnter(menu.id)}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <button
                                        onClick={() => setOpenDropdown(isOpen ? null : menu.id)}
                                        className={`px-2.5 py-1.5 rounded-xl text-[11px] font-black tracking-wider uppercase whitespace-nowrap transition-all flex items-center gap-1 cursor-pointer ${
                                            isOpen
                                                ? 'text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-950/80'
                                                : 'text-slate-700 dark:text-slate-300 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                        }`}
                                    >
                                        <span>{menu.label}</span>
                                        <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180 text-cyan-600' : 'text-slate-400'}`} />
                                    </button>

                                    {/* Mega Menu Popover Card (Matching Image Style) */}
                                    {isOpen && (
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50 animate-fade-in">
                                            {/* Top Triangle Arrow Notch */}
                                            <div className="w-3 h-3 bg-white dark:bg-slate-900 border-t border-l border-slate-200 dark:border-slate-800 rotate-45 mx-auto -mb-1.5 relative z-10" />

                                            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-200/80 dark:border-slate-800 backdrop-blur-xl min-w-[520px] text-left">
                                                <div className="grid grid-cols-2 gap-8">
                                                    {menu.columns.map((col, cIdx) => (
                                                        <div key={cIdx} className="space-y-3">
                                                            <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500 block border-b border-slate-100 dark:border-slate-800 pb-1.5">
                                                                {col.title}
                                                            </span>
                                                            <div className="space-y-1">
                                                                {col.items.map((item, iIdx) => {
                                                                    const IconComp = item.icon;
                                                                    if (item.isMore) {
                                                                        return (
                                                                            <a
                                                                                key={iIdx}
                                                                                href={item.path}
                                                                                onClick={(e) => { e.preventDefault(); handleLinkClick(item.path); }}
                                                                                className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-black text-cyan-600 dark:text-cyan-400 hover:text-cyan-500 hover:underline transition-all cursor-pointer"
                                                                            >
                                                                                <span>{item.label}</span>
                                                                                <span>➔</span>
                                                                            </a>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <a
                                                                            key={iIdx}
                                                                            href={item.path}
                                                                            onClick={(e) => { e.preventDefault(); handleLinkClick(item.path); }}
                                                                            className="group flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/80 text-slate-800 dark:text-slate-200 hover:text-cyan-600 dark:hover:text-cyan-400 transition-all cursor-pointer"
                                                                        >
                                                                            <div className="flex items-center gap-2.5">
                                                                                <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                                                                                    {IconComp ? <IconComp className="w-4 h-4" /> : <span>⚡</span>}
                                                                                </div>
                                                                                <span className="text-xs font-bold tracking-tight">
                                                                                    {item.label}
                                                                                </span>
                                                                            </div>
                                                                            {item.badge && (
                                                                                <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.2 rounded-full bg-cyan-100 dark:bg-cyan-900/60 text-cyan-700 dark:text-cyan-300">
                                                                                    {item.badge}
                                                                                </span>
                                                                            )}
                                                                        </a>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Direct Blog Link */}
                        <a
                            href="/blog"
                            onClick={(e) => { e.preventDefault(); handleLinkClick('/blog'); }}
                            className="px-2.5 py-1.5 rounded-xl text-[11px] font-black tracking-wider uppercase whitespace-nowrap transition-all text-slate-700 dark:text-slate-300 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                        >
                            BLOG
                        </a>
                    </nav>

                    {/* Right Controls: Theme Toggler & Mobile Menu Button */}
                    <div className="flex items-center gap-2.5">
                        <button
                            onClick={toggleDarkMode}
                            className="p-2.5 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all border border-slate-200/80 dark:border-slate-700 shadow-xs flex items-center justify-center cursor-pointer"
                            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                            aria-label="Toggle Theme"
                        >
                            {isDarkMode ? <SunIcon className="w-5 h-5 text-amber-400" /> : <MoonIcon className="w-5 h-5 text-slate-700 dark:text-slate-300" />}
                        </button>

                        {/* Mobile Hamburger Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="lg:hidden p-2.5 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all border border-slate-200/80 dark:border-slate-700 cursor-pointer"
                            aria-label="Toggle navigation menu"
                        >
                            {isMobileMenuOpen ? <XIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Drawer (Accordion Style) */}
            {isMobileMenuOpen && (
                <div className="lg:hidden bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 max-h-[85vh] overflow-y-auto px-4 py-4 space-y-2 text-left animate-fade-in-down">
                    {navMenus.map((menu) => {
                        const isExpanded = mobileExpandedGroup === menu.id;
                        return (
                            <div key={menu.id} className="border-b border-slate-100 dark:border-slate-800/80 pb-2">
                                <button
                                    onClick={() => setMobileExpandedGroup(isExpanded ? null : menu.id)}
                                    className="w-full py-2.5 flex items-center justify-between text-xs font-black uppercase text-slate-900 dark:text-white cursor-pointer"
                                >
                                    <span>{menu.label}</span>
                                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180 text-cyan-600' : 'text-slate-400'}`} />
                                </button>

                                {isExpanded && (
                                    <div className="space-y-4 pt-2 pl-2">
                                        {menu.columns.map((col, cIdx) => (
                                            <div key={cIdx} className="space-y-1.5">
                                                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                                    {col.title}
                                                </span>
                                                <div className="grid grid-cols-1 gap-1">
                                                    {col.items.map((item, iIdx) => {
                                                        const IconComp = item.icon;
                                                        if (item.isMore) {
                                                            return (
                                                                <a
                                                                    key={iIdx}
                                                                    href={item.path}
                                                                    onClick={(e) => { e.preventDefault(); handleLinkClick(item.path); }}
                                                                    className="flex items-center gap-1 py-1 text-xs font-black text-cyan-600 dark:text-cyan-400"
                                                                >
                                                                    <span>{item.label}</span>
                                                                    <span>➔</span>
                                                                </a>
                                                            );
                                                        }
                                                        return (
                                                            <a
                                                                key={iIdx}
                                                                href={item.path}
                                                                onClick={(e) => { e.preventDefault(); handleLinkClick(item.path); }}
                                                                className="flex items-center justify-between p-2 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-200 text-xs font-bold"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    {IconComp && <IconComp className="w-4 h-4" />}
                                                                    <span>{item.label}</span>
                                                                </div>
                                                                {item.badge && (
                                                                    <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.2 rounded-full bg-cyan-100 dark:bg-cyan-900/60 text-cyan-700 dark:text-cyan-300">
                                                                        {item.badge}
                                                                    </span>
                                                                )}
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div className="pt-2">
                        <a
                            href="/blog"
                            onClick={(e) => { e.preventDefault(); handleLinkClick('/blog'); }}
                            className="w-full py-2.5 px-3 rounded-xl bg-cyan-50 dark:bg-cyan-950/60 text-cyan-700 dark:text-cyan-300 font-bold text-xs uppercase flex items-center justify-between"
                        >
                            <span>Blog & Tutorials</span>
                            <span>➔</span>
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};
